import { Button, HStack, VStack } from '@chakra-ui/react';
import { format } from 'date-fns';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';

import { AppError } from '../../../../framework/models/errors';
import { TypeUtils } from '../../../../framework/utils/UtilityTypes';
import { BackButton, SaveButton } from '../../../components/StyledButton';
import { pageKeywords, pagePaths } from '../../../constants/Page';
import { useGlobalStateMachine } from '../../../GlobalStore';
import { useEngeneNavigate } from '../../../hooks/navigate';
import { usePostWorker } from '../../../hooks/queries/worker';
import useToastHook from '../../../hooks/ToastHook';
import { Worker } from '../../../models/gen/worker';
import { Formatter } from '../../../utils/FormatUtils';
import { Validator } from '../../../utils/ValidationUtils';
import { ZipCodeUtils } from '../../../utils/ZipCode';
import { useConfirmModal } from '../../modals/confirm';
import {
  UserInfoAccordion,
  UserInfoState,
} from './accordion/UserInfoAccordion';
import { UserInfoContextType } from './UserInfo';

/**
 * 編集モード
 * @returns
 */
const UserInfoEdit: React.FunctionComponent = () => {
  const { state, actions } = useGlobalStateMachine();
  const navigate = useEngeneNavigate();

  const { ConfirmModal, onOpen } = useConfirmModal<Worker>();
  const form = useFormContext<UserInfoState>();
  const { toast } = useToastHook();
  const { refetch } = useOutletContext<UserInfoContextType>();
  const postWorker = usePostWorker();

  return (
    <VStack paddingBottom={2} width="100%">
      <UserInfoAccordion
        defaultIndex={state.pageInfo.USER_INFO?.openedPanel || 0}
        mode="edit"
      />
      <HStack
        as="form"
        justifyContent="space-evenly"
        onSubmit={form.handleSubmit((initialState: Worker) => {
          // 氏名(必須)
          if (
            // state.pageInfo.USER_INFO?.openedPanel !== 0 &&
            TypeUtils.isNilString(initialState?.name)
          ) {
            toast('氏名を入力してください');
            return;
          }
          // カナ氏名(必須＋全角カナ)
          if (
            // state.pageInfo.USER_INFO?.openedPanel !== 0 &&
            TypeUtils.isNilString(initialState?.nameK)
          ) {
            toast('氏名 (カナ)を入力してください');
            return;
          } else if (
            // state.pageInfo.USER_INFO?.openedPanel !== 0 &&
            !Validator.isZenkakuKatakana(initialState?.nameK)
          ) {
            toast('氏名 (カナ)は全角カタカナと全角スペースで入力してください');
            return;
          }
          // 性別(必須)
          if (!TypeUtils.isNonNilStrict(initialState?.gender)) {
            toast('性別を入力してください');
            return;
          }

          // メールアドレス(Email)
          if (
            TypeUtils.isNonNilString(initialState?.mailAddr) &&
            !Validator.isValidEmail(initialState?.mailAddr)
          ) {
            toast('不正なメールアドレスです', 'warning');
            return;
          }
          // 郵便番号(数値7桁)
          if (
            TypeUtils.isNonNilString(initialState.adderssInfo?.zipCode) &&
            !ZipCodeUtils.isValidNoHyphen(initialState.adderssInfo?.zipCode)
          ) {
            toast('郵便番号は半角数値7桁で入力してください', 'warning');
            console.warn(
              '郵便番号は半角数値7桁で入力してください',
              initialState.adderssInfo?.zipCode
            );
            return;
          }
          // 家族連絡先　郵便番号(7桁)
          if (
            TypeUtils.isNonNilString(
              initialState.addressInfoOffamily?.zipCode
            ) &&
            !ZipCodeUtils.isValidNoHyphen(
              initialState.addressInfoOffamily?.zipCode
            )
          ) {
            toast(
              '郵便番号(家族連絡先)は半角数値7桁で入力してください',
              'warning'
            );
            console.warn(
              '郵便番号(家族連絡先)は半角数値7桁で入力してください',
              initialState.addressInfoOffamily?.zipCode
            );
            return;
          }
          // 血圧(最高)>=血圧(最低) ※片方だけ入力を許す
          // ※編集した値は文字列として取得されることを考慮する必要あり
          if (
            TypeUtils.isNonNilStrict(
              initialState?.workerReference?.bloodPressureMax
            ) &&
            TypeUtils.isNonNilStrict(
              initialState?.workerReference?.bloodPressureMin
            ) &&
            !Validator.lessThan(
              initialState?.workerReference?.bloodPressureMin as
                | number
                | string,
              initialState?.workerReference?.bloodPressureMax as number | string
            )
          ) {
            toast('血圧(最高)は血圧(最低)以上となるよう入力してください');
            return;
          }

          onOpen(initialState);
        })}
        width="80%"
      >
        <BackButton
          onClick={() => {
            form.reset();
            navigate(`${pagePaths.USER_INFO}/${pageKeywords.mode.VIEW}`);
          }}
        />
        <SaveButton /* isDisabled={isSaveDisabled} */ />
      </HStack>
      <ConfirmModal
        message="保存してよろしいですか?"
        onOk={(worker) => {
          if (TypeUtils.isNonNil(worker)) {
            actions.setGuard(true);
            postWorker.mutate(
              {
                ...worker,
                gender: worker.gender || 0,
                dateOfBirth: Formatter.date(worker.dateOfBirth, true),
                hiredDate: Formatter.date(worker.hiredDate, true),
                firstDateOfPayRaise: Formatter.date(
                  worker?.firstDateOfPayRaise,
                  true
                ),
                adderssInfo: {
                  ...worker.adderssInfo,
                  zipCode: ZipCodeUtils.removeHyphen(
                    worker.adderssInfo?.zipCode
                  ),
                },
                addressInfoOffamily: {
                  ...worker.addressInfoOffamily,
                  zipCode: ZipCodeUtils.removeHyphen(
                    worker.addressInfoOffamily?.zipCode
                  ),
                },
                workerReference: {
                  ...worker.workerReference,
                  startDateOfExperience: Formatter.date(
                    worker.workerReference?.startDateOfExperience,
                    true
                  ),
                  lastHealthCheckDate: Formatter.date(
                    worker.workerReference?.lastHealthCheckDate,
                    true
                  ),
                  specialHealthCheckDate: Formatter.date(
                    worker.workerReference?.specialHealthCheckDate,
                    true
                  ),
                },
              },
              {
                onSuccess: () => {
                  navigate(`${pagePaths.USER_INFO}/${pageKeywords.mode.VIEW}`);
                  refetch();
                  actions.setGuard(false);
                },
                onError: (error) => {
                  toast(
                    error instanceof AppError
                      ? error
                      : '職人情報の保存に失敗しました'
                  );
                  actions.setGuard(false);
                },
              }
            );
          }
        }}
      />
    </VStack>
  );
};

export default UserInfoEdit;
